import { useState } from 'react';
import { Popconfirm } from 'antd';

import type { DueExpense, RecurringExpense } from '@/api/cash';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { dueWords, scheduleWords } from '@core/cash/recurring';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import type { Session } from '@core/session/types';

/**
 * What is due (Cash &amp; bank step 8) — overdue first, then up to a week ahead, as the Expenses
 * board's "Due this week" (canvas "Cash & expense flow", board 2). Only a template's earliest
 * occurrence offers Record and Skip; a later one waits behind it, so none is missed. Nothing here
 * posts: Record opens the voucher, filled in.
 */
export function DueCard({
  session,
  due,
  today,
  busy,
  onRecord,
  onSkip,
  onAll,
}: {
  session: Session;
  due: DueExpense[];
  today: string;
  busy: boolean;
  onRecord: (item: DueExpense) => void;
  onSkip: (item: DueExpense) => void;
  /** Opens the recurring expenses themselves. */
  onAll?: () => void;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="mb-0 text-[15px] font-bold text-gray-900">Due this week</h2>
        {onAll && (
          <button
            type="button"
            onClick={onAll}
            className="text-primary cursor-pointer text-[13px] font-semibold hover:underline"
          >
            Recurring →
          </button>
        )}
      </div>
      <p className="text-default mt-0.5 mb-3 text-[length:var(--scale-list-meta)]">
        Listed, never posted. Record each one when it is paid.
      </p>

      {due.length === 0 ? (
        <EmptyState icon="icon icon-calendar-check" title="Nothing is due" />
      ) : (
        <ul className="flex flex-col gap-2.5 ps-0">
          {due.map((item) => {
            const words = dueWords(item.dueOn, today);
            const late = words.tone === 'warning';

            return (
              <li
                key={`${item.recurringExpenseId}-${item.dueOn}`}
                className={`list-none rounded-xl border p-3 ${
                  late ? 'border-warning/30 bg-warning-transparent' : 'border-border-color bg-white'
                }`}
              >
                <div className="flex justify-between gap-2">
                  <span className="text-[13px] font-semibold text-gray-900">{item.name}</span>
                  <span className="tabular-nums text-[13px] font-semibold text-gray-900">
                    ₹{formatAmount(item.typicalAmount)}
                  </span>
                </div>
                <p className={`mb-0 text-[12px] ${late ? 'text-warning' : 'text-default'}`}>
                  {words.text} · {formatDate(item.dueOn)} · {paymentMethodLabel[item.method]}
                  {item.cashAccountName ? ` from ${item.cashAccountName}` : ''}
                </p>
                {item.isNext ? (
                  <WriteAction session={session} required="Expense:Create">
                    {(disabled) => (
                      <div className="mt-2.5 flex gap-2">
                        <Button
                          variant="primary"
                          loading={busy}
                          disabled={disabled || busy}
                          onClick={() => onRecord(item)}
                        >
                          Record
                        </Button>
                        <Popconfirm
                          title="Skip this one?"
                          description={
                            <p className="text-default w-60 pt-1 text-[12px]">
                              It was paid some other way. It stops being due, and the next one takes
                              its place. Nothing is posted.
                            </p>
                          }
                          okText="Skip"
                          onConfirm={() => onSkip(item)}
                          disabled={disabled || busy}
                        >
                          <Button disabled={disabled || busy}>Skip this one</Button>
                        </Popconfirm>
                      </div>
                    )}
                  </WriteAction>
                ) : (
                  <p className="text-default mt-1 mb-0 text-[12px]">After the earlier one</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/** The branch's recurring expenses, each with its schedule and next due day. */
export function RecurringCard({
  session,
  templates,
  loading,
  onEdit,
  onNew,
}: {
  session: Session;
  templates: RecurringExpense[];
  loading: boolean;
  onEdit: (id: number) => void;
  onNew: () => void;
}) {
  // Retired ones are history; behind a switch, and after the live ones, so a shop's list does not
  // fill with them (2026-10-23).
  const [showRetired, setShowRetired] = useState(false);
  const shown = templates
    .filter((template) => showRetired || template.isActive)
    .sort((a, b) => Number(b.isActive) - Number(a.isActive));
  const retired = templates.filter((template) => !template.isActive).length;

  const columns: DataViewColumn<RecurringExpense>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      primary: true,
      render: (_: unknown, template: RecurringExpense) => (
        <span className="flex flex-col">
          <span className="flex items-center gap-1.5 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {template.name}
            {!template.isActive && <StatusBadge tone="neutral">Retired</StatusBadge>}
          </span>
          <span className="text-default text-[length:var(--scale-list-meta)]">
            {template.headName}
          </span>
        </span>
      ),
    },
    {
      title: 'When',
      dataIndex: 'frequency',
      render: (_: unknown, template: RecurringExpense) => (
        <span className="flex flex-col">
          <span>{scheduleWords(template)}</span>
          <span className="text-default text-[length:var(--scale-list-meta)]">
            {template.nextDueOn ? `Next ${formatDate(template.nextDueOn)}` : 'Not falling due'}
          </span>
        </span>
      ),
    },
    {
      title: 'Usually',
      dataIndex: 'typicalAmount',
      align: 'right',
      render: (value: number) => formatAmount(value),
    },
    {
      title: '',
      key: 'actions',
      trailing: true,
      render: (_: unknown, template: RecurringExpense) => (
        <WriteAction session={session} required="Expense:Edit">
          {(disabled) => (
            <Button disabled={disabled} onClick={() => onEdit(template.id)}>
              Edit
            </Button>
          )}
        </WriteAction>
      ),
    },
  ];

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">Recurring</p>
        <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 whitespace-nowrap">
          {retired > 0 && (
            <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-900">
              <input
                type="checkbox"
                checked={showRetired}
                onChange={(event) => setShowRetired(event.target.checked)}
              />
              Show retired ({retired})
            </label>
          )}
          <WriteAction session={session} required="Expense:Create">
            {(disabled) => (
              <Button icon="icon icon-plus" disabled={disabled} onClick={onNew}>
                New recurring expense
              </Button>
            )}
          </WriteAction>
        </span>
      </div>

      <DataView
        columns={columns}
        dataSource={shown}
        loading={loading}
        empty={
          <EmptyState
            icon="icon icon-repeat"
            title="Nothing recurs yet"
            hint="Rent on the 5th, electricity, a yearly licence — set them up once and they fall due here."
          />
        }
      />
    </Card>
  );
}
